import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { instructorsApi } from '../../api/instructors'
import { subscriptionsApi } from '../../api/subscriptions'
import { useAuth } from '../../context/AuthContext'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import CourseCard from '../../components/course/CourseCard'
import { FiStar, FiUsers, FiBook, FiMessageCircle, FiPhone, FiShare2, FiFacebook, FiTwitter, FiLinkedin, FiSend, FiMail } from 'react-icons/fi'
import { FaGraduationCap } from 'react-icons/fa'
import './InstructorProfile.css'

const InstructorProfile = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [activeTab, setActiveTab] = useState('courses')

  const { data, isLoading, error } = useQuery({
    queryKey: ['instructor', id],
    queryFn: () => instructorsApi.getInstructor(id),
    enabled: !!id,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
  })

  const subscribeMutation = useMutation({
    mutationFn: (data) => subscriptionsApi.createSubscription(data),
    onSuccess: () => {
      alert('تم الاشتراك بنجاح')
    },
    onError: (error) => {
      console.error('Subscription error:', error)
      alert('حدث خطأ أثناء الاشتراك')
    },
  })

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  // Handle 401 errors gracefully - show login required instead of error
  if (error?.response?.status === 401) {
    const instructor = data?.data || {}
    return (
      <div className="instructor-profile-page bg-gray-900 min-h-screen">
        <Header />
        <main className="instructor-profile-main bg-gray-900">
          <div className="container mx-auto px-4 py-8">
            {/* Login Required Banner */}
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى ملف المدرس والاشتراك</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
            
            {/* Show basic instructor info if available */}
            {instructor.name && (
              <div className="profile-header-card">
                <div className="profile-image-section">
                  {instructor.avatar ? (
                    <img
                      src={getStorageUrl(instructor.avatar)}
                      alt={instructor.name}
                      className="profile-image-large"
                    />
                  ) : (
                    <div className="profile-image-placeholder">
                      {instructor.name?.[0]?.toUpperCase() || 'I'}
                    </div>
                  )}
                </div>
                <div className="profile-details-section">
                  {instructor.subject?.name && (
                    <div className="subject-badge">
                      <FaGraduationCap className="subject-icon" />
                      <span>{instructor.subject.name}</span>
                    </div>
                  )}
                  <h1 className="profile-name">{instructor.name}</h1>
                  <p className="profile-title">{instructor.bio || 'خبير في التعليم'}</p>
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  const instructor = data?.data

  if (error || !instructor) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>الخبير غير موجود</p>
        </div>
        <Footer />
      </>
    )
  }

  const handleSubscribe = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    subscribeMutation.mutate({ instructor_id: id })
  }

  const handleMessage = () => {
    if (instructor.phone) {
      window.open(`https://wa.me/${instructor.phone.replace(/\D/g, '')}`, '_blank')
    }
  }

  const handleCall = () => {
    if (instructor.phone) {
      window.location.href = `tel:${instructor.phone}`
    }
  }

  const handleShare = (platform) => {
    const url = window.location.href
    const text = `${instructor.name} - ${instructor.bio || 'خبير في التعليم'}`
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    }
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank')
    }
  }

  const shareUrl = window.location.href

  return (
    <div className="instructor-profile-page bg-gray-900 min-h-screen">
      <Header />
      <main className="instructor-profile-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          {/* Login Required Banner */}
          {!isAuthenticated && (
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى ملف المدرس والاشتراك</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
          )}
          
          {/* Profile Header Card */}
          <div className="profile-header-card">
            {/* Image Section (Right) */}
            <div className="profile-image-section">
              {instructor.avatar ? (
                <img
                  src={getStorageUrl(instructor.avatar)}
                  alt={instructor.name}
                  className="profile-image-large"
                />
              ) : (
                <div className="profile-image-placeholder">
                  {instructor.name?.[0]?.toUpperCase() || 'I'}
                </div>
              )}
            </div>

            {/* Details Section (Left) */}
            <div className="profile-details-section">
              {/* Subject Badge */}
              {instructor.subject?.name && (
                <div className="subject-badge">
                  <FaGraduationCap className="subject-icon" />
                  <span>{instructor.subject.name}</span>
                </div>
              )}

              {/* Instructor Name */}
              <h1 className="profile-name">{instructor.name}</h1>

              {/* Title/Role */}
              <p className="profile-title">{instructor.bio || 'خبير في التعليم'}</p>

              {/* Teaching Levels Tags - if available */}
              {instructor.levels && instructor.levels.length > 0 && (
                <div className="teaching-levels">
                  {instructor.levels.map((level, index) => (
                    <span key={index} className="level-tag">
                      {level}
                    </span>
                  ))}
                </div>
              )}

              {/* Social Media Links */}
              {(instructor.facebook || instructor.telegram || instructor.twitter || instructor.linkedin) && (
                <div className="social-links-section">
                  <p className="social-links-label">لينكات وسائل التواصل الخاصة بالمدرس</p>
                  <div className="social-links-icons">
                    {instructor.facebook && (
                      <a href={instructor.facebook} target="_blank" rel="noopener noreferrer" className="social-link-icon">
                        <FiFacebook />
                      </a>
                    )}
                    {instructor.telegram && (
                      <a href={instructor.telegram} target="_blank" rel="noopener noreferrer" className="social-link-icon">
                        <FiSend />
                      </a>
                    )}
                    {instructor.twitter && (
                      <a href={instructor.twitter} target="_blank" rel="noopener noreferrer" className="social-link-icon">
                        <FiTwitter />
                      </a>
                    )}
                    {instructor.linkedin && (
                      <a href={instructor.linkedin} target="_blank" rel="noopener noreferrer" className="social-link-icon">
                        <FiLinkedin />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Subscribe Button */}
              <button onClick={handleSubscribe} className="subscribe-button" disabled={subscribeMutation.isLoading}>
                {subscribeMutation.isLoading ? 'جاري الاشتراك...' : 'اشترك الان مع هذا المدرس'}
              </button>

              {/* Contact Section */}
              <div className="share-section">
                <p className="share-label">تواصل مع المدرس</p>
                <div className="share-buttons">
                  {instructor.phone ? (
                    <>
                      <button onClick={handleCall} className="contact-btn call">
                        <FiPhone /> اتصال
                      </button>
                      <button onClick={handleMessage} className="contact-btn whatsapp">
                        <FiMessageCircle /> واتساب
                      </button>
                    </>
                  ) : (
                    <span className="text-gray-400 text-sm">غير متاح حالياً</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="statistics-row">
            <div className="stat-card">
              <FaGraduationCap className="stat-icon" />
              <div className="stat-number">{instructor.total_courses || 0}</div>
              <div className="stat-label">دورة</div>
            </div>
            <div className="stat-card">
              <FiUsers className="stat-icon" />
              <div className="stat-number">{instructor.total_students * 10 || 0}</div>
              <div className="stat-label">طالب</div>
            </div>
            <div className="stat-card">
              <FiStar className="stat-icon" />
              <div className="stat-number">
                {instructor.rating ? instructor.rating.toFixed(1) : '4.9'}
              </div>
              <div className="stat-label">تقييم</div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-navigation">
            <button
              className={`tab-nav-btn ${activeTab === 'courses' ? 'active' : ''}`}
              onClick={() => setActiveTab('courses')}
            >
              <FiBook /> الدورات
            </button>
            <button
              className={`tab-nav-btn ${activeTab === 'students' ? 'active' : ''}`}
              onClick={() => setActiveTab('students')}
            >
              <FiUsers /> الطلاب
            </button>
            <button
              className={`tab-nav-btn ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              <FiStar /> التقييمات
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content-area">
            {activeTab === 'courses' && (
              <div className="tab-content-section">
                {instructor.courses && instructor.courses.length > 0 ? (
                  <div className="courses-grid-profile">
                    {instructor.courses.map((course) => (
                      <CourseCard key={course.id} course={course} />
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-message">
                    <p>لا توجد دورات متاحة</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'students' && (
              <div className="tab-content-section">
                <div className="empty-state-message">
                  <FiUsers className="empty-icon" />
                  <p>قائمة الطلاب قريباً</p>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="tab-content-section">
                <div className="empty-state-message">
                  <FiStar className="empty-icon" />
                  <p>التقييمات قريباً</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default InstructorProfile
